import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { availabilityMeta, remoteLabel, rateRange } from "@/lib/marketplace";
import { Storefront, MagnifyingGlass, MapPin, Briefcase, Clock, ChatCircleDots, UserCircle, Buildings, Translate, BookmarkSimple, PaperPlaneTilt } from "@phosphor-icons/react";

const inputCls = "bg-surface-2 border-border text-fg";
const ANY = "__all__";

function AvailBadge({ status }) {
  const a = availabilityMeta(status);
  return (
    <span className="shrink-0 flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-full border"
      style={{ color: a.color, borderColor: a.color + "55", background: a.color + "14" }} data-testid="mk-avail-badge">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: a.color }} /> {a.label}
    </span>
  );
}

function ProfileCard({ p, onOpen }) {
  return (
    <button onClick={() => onOpen(p)} data-testid={`mk-card-${p.marketplace_profile_id}`}
      className="surface rounded-lg p-5 text-left hover-lift w-full">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-8 h-8 rounded-full bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0">
            <UserCircle size={18} weight="duotone" className="text-accent" />
          </span>
          <div className="min-w-0">
            <h3 className="font-display font-bold text-fg truncate">{p.display_name}</h3>
            <div className="text-xs text-muted truncate">{p.headline || p.role || "—"} · {p.seniority || "—"}</div>
          </div>
        </div>
        <AvailBadge status={p.availability_status} />
      </div>
      {p.canonical_family_name && (
        <div className="mt-3">
          <span className="text-[11px] font-mono uppercase tracking-wide text-accent bg-accent/10 border border-accent/20 rounded px-2 py-0.5">{p.canonical_family_name}</span>
        </div>
      )}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(p.skills || []).slice(0, 6).map((s) => (
          <span key={s} className="text-xs font-mono bg-surface-2 text-fg/80 border border-border px-2 py-0.5 rounded">{s}</span>
        ))}
      </div>
      <div className="flex items-center gap-3 text-xs text-muted mt-3 flex-wrap">
        {(p.city || p.region) && <span className="flex items-center gap-1"><MapPin size={12} /> {p.city || p.region}</span>}
        {p.years_experience ? <span className="flex items-center gap-1"><Briefcase size={12} /> {p.years_experience} ans</span> : null}
        <span className="flex items-center gap-1"><Clock size={12} /> {remoteLabel(p.remote_preference)}</span>
        {rateRange(p.daily_rate_min, p.daily_rate_max, p.currency) && (
          <span className="font-mono text-fg/80">{rateRange(p.daily_rate_min, p.daily_rate_max, p.currency)}</span>
        )}
      </div>
      <div className="mt-4 pt-3 border-t border-border flex items-center gap-1.5 text-xs text-muted">
        <Buildings size={13} /> {p.organization_name ? p.organization_name : "Organisation confidentielle"}
      </div>
    </button>
  );
}

export default function MarketplaceBrowse() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [facets, setFacets] = useState({ families: [], seniorities: [], availabilities: [], cities: [] });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [family, setFamily] = useState(ANY);
  const [seniority, setSeniority] = useState(ANY);
  const [availability, setAvailability] = useState(ANY);
  const [remote, setRemote] = useState(ANY);
  const [selected, setSelected] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  const loadFacets = useCallback(async () => {
    try { const { data } = await api.get("/marketplace/facets"); setFacets(data); } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (family !== ANY) params.set("family", family);
      if (seniority !== ANY) params.set("seniority", seniority);
      if (availability !== ANY) params.set("availability", availability);
      if (remote !== ANY) params.set("remote", remote);
      params.set("limit", "48");
      const { data } = await api.get(`/marketplace/profiles?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally { setLoading(false); }
  }, [q, family, seniority, availability, remote]);

  useEffect(() => { loadFacets(); }, [loadFacets]);
  useEffect(() => { const t = setTimeout(() => load(), 250); return () => clearTimeout(t); }, [load]);

  const resetFilters = () => { setQ(""); setFamily(ANY); setSeniority(ANY); setAvailability(ANY); setRemote(ANY); };
  const hasFilters = q.trim() || family !== ANY || seniority !== ANY || availability !== ANY || remote !== ANY;

  const openProfile = async (p) => {
    setSelected(p); setMsg("");
    try { const { data } = await api.get(`/marketplace/profiles/${p.marketplace_profile_id}`); setSelected(data); } catch { /* keep */ }
  };

  const contact = async () => {
    if (!msg.trim()) { toast.error("Écrivez un message avant d'envoyer."); return; }
    setSending(true);
    try {
      await api.post("/marketplace/conversations", { profile_id: selected.marketplace_profile_id, message: msg.trim() });
      toast.success("Message envoyé via la messagerie interne");
      setSelected(null);
      navigate("/app/marketplace/messages");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally { setSending(false); }
  };

  const saveSearch = async () => {
    const criteria = {
      q: q.trim(), family: family !== ANY ? family : "", seniority: seniority !== ANY ? seniority : "",
      availability: availability !== ANY ? availability : "", remote: remote !== ANY ? remote : "",
    };
    const name = criteria.q || criteria.family || criteria.seniority || "Ma recherche";
    try {
      await api.post("/marketplace/saved-searches", { name, criteria, alerts_enabled: true, instant: true });
      toast.success("Recherche enregistrée — vous serez alerté des nouveaux profils correspondants");
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); }
  };

  return (
    <div data-testid="marketplace-browse">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-2 flex items-center gap-2"><Storefront size={14} className="text-accent" /> Marketplace de talents</div>
          <h1 className="font-display text-3xl font-bold">Consultants disponibles <span className="text-muted text-lg font-mono">({total})</span></h1>
          <p className="text-muted text-sm mt-1">Découvrez les profils publiés par les ESN. Contactez via la messagerie interne — les coordonnées restent privées.</p>
        </div>
      </div>

      <div className="surface rounded-lg p-4 mb-6" data-testid="marketplace-filters">
        <div className="relative mb-3">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="marketplace-search"
            placeholder="Rechercher un métier, une compétence (ex. DevOps, React, Data Engineer)…"
            className="w-full bg-surface-2 border border-border rounded-md pl-9 pr-4 py-2.5 text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Select value={family} onValueChange={setFamily}>
            <SelectTrigger className={inputCls} data-testid="marketplace-filter-family"><SelectValue placeholder="Famille métier" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Toutes les familles</SelectItem>
              {facets.families.map((f) => <SelectItem key={f.value} value={f.value}>{f.value} ({f.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={seniority} onValueChange={setSeniority}>
            <SelectTrigger className={inputCls} data-testid="marketplace-filter-seniority"><SelectValue placeholder="Séniorité" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Toutes séniorités</SelectItem>
              {facets.seniorities.map((s) => <SelectItem key={s.value} value={s.value}>{s.value} ({s.count})</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={availability} onValueChange={setAvailability}>
            <SelectTrigger className={inputCls} data-testid="marketplace-filter-availability"><SelectValue placeholder="Disponibilité" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Toutes dispos</SelectItem>
              <SelectItem value="AVAILABLE_NOW">Disponible</SelectItem>
              <SelectItem value="AVAILABLE_SOON">Bientôt disponible</SelectItem>
              <SelectItem value="AVAILABLE_FROM_DATE">Disponible à une date</SelectItem>
            </SelectContent>
          </Select>
          <Select value={remote} onValueChange={setRemote}>
            <SelectTrigger className={inputCls} data-testid="marketplace-filter-remote"><SelectValue placeholder="Mode de travail" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Tous modes</SelectItem>
              <SelectItem value="ONSITE">Sur site</SelectItem>
              <SelectItem value="HYBRID">Hybride</SelectItem>
              <SelectItem value="REMOTE">Télétravail</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-3 mt-3">
          <button onClick={saveSearch} data-testid="marketplace-save-search" className="text-xs text-accent hover:underline flex items-center gap-1">
            <BookmarkSimple size={13} /> Enregistrer cette recherche & créer une alerte
          </button>
          {hasFilters && <button onClick={resetFilters} data-testid="marketplace-reset-filters" className="text-xs text-muted hover:text-fg">Réinitialiser</button>}
        </div>
      </div>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-12 text-center" data-testid="marketplace-empty">
          <Storefront size={40} weight="duotone" className="text-accent mx-auto mb-3" />
          <div className="font-display font-bold text-fg mb-1">Aucun profil ne correspond</div>
          <p className="text-muted text-sm">{hasFilters ? "Élargissez vos critères de recherche." : "Aucun consultant n'est publié dans la Marketplace pour le moment."}</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="marketplace-grid">
          {items.map((p) => <ProfileCard key={p.marketplace_profile_id} p={p} onOpen={openProfile} />)}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-surface border-border max-w-lg max-h-[90vh] overflow-y-auto" data-testid="marketplace-profile-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-2">
                  <UserCircle size={22} weight="duotone" className="text-accent" /> {selected.display_name}
                </DialogTitle>
                <DialogDescription className="text-muted">{selected.headline || selected.role} · {selected.seniority}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <AvailBadge status={selected.availability_status} />
                  {selected.canonical_family_name && (
                    <span className="text-[11px] font-mono uppercase tracking-wide text-accent bg-accent/10 border border-accent/20 rounded px-2 py-0.5">{selected.canonical_family_name}</span>
                  )}
                </div>
                {selected.summary && <p className="text-sm text-fg/90 whitespace-pre-line">{selected.summary}</p>}
                {(selected.skills || []).length > 0 && (
                  <div>
                    <div className="overline mb-1.5">Compétences</div>
                    <div className="flex flex-wrap gap-1.5">
                      {selected.skills.map((s) => <span key={s} className="text-xs font-mono bg-surface-2 text-fg/80 border border-border px-2 py-0.5 rounded">{s}</span>)}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><div className="overline mb-0.5">Localisation</div><div className="text-fg/90 flex items-center gap-1"><MapPin size={13} className="text-muted" /> {selected.city || selected.region || "—"}</div></div>
                  <div><div className="overline mb-0.5">Mode de travail</div><div className="text-fg/90">{remoteLabel(selected.remote_preference)}</div></div>
                  {selected.years_experience ? <div><div className="overline mb-0.5">Expérience</div><div className="text-fg/90">{selected.years_experience} ans</div></div> : null}
                  {rateRange(selected.daily_rate_min, selected.daily_rate_max, selected.currency) && (
                    <div><div className="overline mb-0.5">TJM indicatif</div><div className="text-fg/90 font-mono">{rateRange(selected.daily_rate_min, selected.daily_rate_max, selected.currency)}</div></div>
                  )}
                  {(selected.languages || []).length > 0 && (
                    <div className="col-span-2"><div className="overline mb-0.5 flex items-center gap-1"><Translate size={12} /> Langues</div><div className="text-fg/90">{selected.languages.join(", ")}</div></div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs text-muted pt-1 border-t border-border">
                  <Buildings size={13} /> {selected.organization_name ? selected.organization_name : "Organisation confidentielle — révélée au contact"}
                </div>

                <div className="pt-1">
                  <div className="overline mb-1.5 flex items-center gap-1"><ChatCircleDots size={13} className="text-accent" /> Contacter via la messagerie interne</div>
                  <textarea value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="marketplace-contact-message"
                    placeholder="Décrivez votre besoin (contexte de mission, durée, démarrage…)"
                    className="w-full bg-surface-2 border border-border rounded-md text-sm px-3 py-2 min-h-[80px] resize-y text-fg" />
                  <button onClick={contact} disabled={sending} data-testid="marketplace-contact-btn"
                    className="mt-2 w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold py-3 rounded-md hover-lift disabled:opacity-60">
                    <PaperPlaneTilt size={16} weight="fill" /> {sending ? "Envoi…" : "Envoyer le message"}
                  </button>
                  <p className="text-[11px] text-muted text-center mt-2">Les email, téléphone et LinkedIn ne sont jamais exposés.</p>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
